
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import {
  addGammaDataSource,
  castValueType,
  createGeoJSONCircle,
  createGeoJSONSquare,
  emptyGeoJSON,
  formatValue,
  getCurrencySign,
  getRagIconUrl,
  pascalCase,
  resolveBaseMapStyle,
  updateGammaDataSourceData,
  wrapDrawFeature,
} from "@/util/helpers";
import { debounce, get, isEqual, pick, set } from "lodash-es";
import { BottomTab, GDT, IRiskAccuracy, MapPosition } from "@/types";
import { addComponent } from "@/util/add.component";
import VerticalMenu from "@/components/shared/VerticalMenu.vue";
import { CombinedVueInstance } from "vue/types/vue";
import { LayerControl } from "@/types/LayerControl";
import { AnyLocation, ILocation } from "@/types/location";
import { Route } from "vue-router";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import MapMarker from "@/components/map/MapMarker.vue";
import RightSidebarMenu from "@/components/drawers/right/RightSidebarMenu.vue";
import MapLegend from "@/components/map/MapLegend.vue";
import AccumulationsMenu from "@/components/accumulations/AccumulationsMenu.vue";
import { MountingPortal } from "portal-vue";
import EditingLayerIndicator from "@/components/map/EditingLayerIndicator.vue";
import LayerEditTools from "@/components/map/LayerEditTools.vue";
import {
  DataBoundary,
  useDataSource,
  useGammaData,
  useGammaWebServices,
  useGroupedRecords,
  useLayout,
  useLoading,
  useLocation,
  useMap,
  useMeasuringTool,
  useOrgSettings,
} from "@/services";
import { mapState } from "pinia";
import { defineComponent } from "vue";
import { getUrlQueryParam } from "@/router";
import useRiskGeometry from "@/composeable/risk-geometry";
import styles from "@/util/DrawModes/drawStyles";
import { enable as enableModes } from "mapbox-gl-draw-geodesic";
import { RiskDrawPolygon } from "@/util/DrawModes/riskDrawPolygon";
import { Feature, MultiPolygon, Point, Polygon } from "geojson";

const DrawRadiusCircle =
  require("../../util/DrawModes/drawRadiusCircle").default;
const patchDirectSelect =
  require("../../util/DrawModes/patchDirectSelect").default;
const patchDrawPolygon =
  require("../../util/DrawModes/patchDrawPolygon").default;

interface LocationMarker {
  center?: mapboxgl.LngLat;
  draggable: boolean;
}

export default defineComponent({
  name: "MapboxMap",
  components: {
    MapLegend,
    RightSidebarMenu,
    MountingPortal,
    AccumulationsMenu,
    EditingLayerIndicator,
    LayerEditTools,
  },
  setup() {
    const riskGeometry = useRiskGeometry();

    return { riskGeometry };
  },
  data: () => ({
    map: null as mapboxgl.Map | null,
    hooks: {
      "map:layers:updateLayer": 0,
    } as Record<string, number>,
    popup: null as null | mapboxgl.Popup,
    locationMarker: null as null | mapboxgl.Marker,
    contextObj: null as null | mapboxgl.MapLayerMouseEvent,
    measuringService: null as any,
    mapDraw: null as null | typeof MapboxDraw,
    onMapIdle: [] as any[],
    showMapControls: false,
    mapTransitions: false,
    hoveredFeatureId: {} as Record<string, any>,
    boundaryTick: () => void 0 as any,
    mapResizer: null as any,
    layersWithInteractions: new Set<string>(),
    disableLocationZoom: false,
  }),
  computed: {
    ...mapState(useOrgSettings, {
      orgSettings: "settings",
    }),
    ...mapState(useOrgSettings, [
      "selectedBusinessLine",
      "currencySymbol",
      "applicationSettings",
    ]),
    isLoadingData(): boolean {
      return useLoading().isLoadingData("map");
    },
    ...mapState(useMap, [
      "baseMapIndex",
      "isMapLoading",
      "mapPosition",
      "layerControls",
      "dataBoundary",
      "baseMap",
      "mapSettings",
      "mapOptions",
      "editingMode",
      "isEditingCoordinates",
      "onBoundsUpdated",
      "loadedBooks",
    ]),
    ...mapState(useLayout, [
      "page",
      "drawerHeight",
      "isLeftSidebarOpen",
      "isDrawToolOpen",
      "isMapPage",
    ]),
    dataSources(): string[] {
      return useGammaData().dataSources;
    },
    filterOperators(): Record<string, GDT.FilterOperatorOption[]> {
      return useGammaData().filterOperators;
    },
    address(): any {
      return useLocation().address;
    },
    jbaAccuracy(): IRiskAccuracy | undefined {
      return useGammaWebServices().jbaAccuracy;
    },
    activeDatasetBoundary(): Polygon | MultiPolygon | null {
      return this.dataBoundary?.geometry || null;
    },
    mapBounds(): Polygon | MultiPolygon | null {
      return useMap().screenBoundary;
    },
    currentMapPosition: {
      get(): MapPosition {
        return this.mapPosition;
      },
      set(position: MapPosition) {
        if (!isEqual(this.mapPosition, position)) {
          useMap().setMapPosition(position);
        }
      },
    },
    markerCoordinates(): mapboxgl.LngLat | null {
      return useMap().markerPosition;
    },
    mapHeight(): string {
      return `calc(100vh - 56px - ${this.drawerHeight}px)`;
    },
    currentLocation: {
      get(): AnyLocation | null {
        return useLocation().location as AnyLocation | null;
      },
      set(location: AnyLocation | null) {
        if (!isEqual(location, this.currentLocation?._data)) {
          useLocation().updateLocation(location);
        }
      },
    },
    isMarkerEditable(): boolean {
      return !!(this.map && this.isEditingCoordinates);
    },
    activeDatasets(): boolean[] {
      return this.layerControls.map(
        ({ id, isVisible }) => isVisible && this.isDataLayerControl(id)
      );
    },
  },
  watch: {
    baseMapIndex: {
      async handler() {
        this.disableLocationZoom = true;
        await this.loadMap();
      },
    },
    selectedBusinessLine: {
      async handler(line) {
        const { loadLayerGroups, hideAllLayers, layerGroups } = useMap();
        if (layerGroups.length) {
          hideAllLayers();
          await this.$nextTick();
        }

        loadLayerGroups(line.layerGroups);
      },
    },
    orgSettings: {
      async handler(val) {
        if (val && this.baseMap) {
          await this.loadMap();
        }
      },
      immediate: true,
    },
    loadedBooks: {
      async handler(books: Map<string, any>) {
        Array.from(books.keys())
          .filter((x) => !this.layersWithInteractions.has(x))
          .forEach((x) => this.addLayerInteractions(x, x));
      },
      immediate: true,
    },
    isMarkerEditable: {
      handler() {
        if (this.map) {
          this.addLocationMarker();
        }
      },
    },
    mapPosition: {
      handler(val) {
        if (this.map && this.mapTransitions) {
          this.map.easeTo(val);
        }
        this.mapTransitions = false;
      },
    },
    markerCoordinates: {
      handler(val: mapboxgl.LngLatLike) {
        if (this.locationMarker && val) {
          this.locationMarker.setLngLat(val);
        }
      },
      deep: true,
    },
    currentLocation: {
      handler(val) {
        if (this.map) {
          const center = this.map.getCenter();
          if (
            val?.geometry.coordinates &&
            !isEqual(val.geometry.coordinates, [center.lng, center.lat]) &&
            val.geometry.type !== "Polygon"
          ) {
            this.setMapPosition({
              center: val.geometry.coordinates,
            });
            useLayout().isLeftSidebarOpen = true;
            if (!this.disableLocationZoom) {
              this.flyToLocation();
            } else {
              this.disableLocationZoom = false;
            }
          } else {
            this.locationMarker?.remove();
            this.resetHighlight();
          }
        }
      },
      deep: true,
    },
    drawerHeight: {
      handler() {
        setTimeout(() => this.map?.resize(), 300);
      },
    },
    jbaAccuracy: {
      handler() {
        this.updateJbaAccuracy();
      },
    },
    $route(to) {
      this.updateRouteQuery(to);
    },
    editingMode(val) {
      // changes to editing mode
      if (this.mapDraw) {
        this.refreshDrawMode(val.mode);
      }
    },
    mapBounds(newVal, oldVal) {
      if (
        newVal?.coordinates &&
        !isEqual(newVal?.coordinates, oldVal?.coordinates)
      ) {
        this.boundaryTick();
      }
    },
    dataBoundary(feature: DataBoundary) {
      this.updateDataBoundary(feature);
      if (feature) {
        (this.layerControls as LayerControl[]).forEach((layerControl) =>
          this.updateLayers(layerControl)
        );
      }
    },
    activeDatasets() {
      this.boundaryTick();
    },
    activeDatasetBoundary(newVal) {
      if (
        !isEqual(newVal, useMap().activeDatasetBoundary) &&
        this.editingMode.mode === "simple_select"
      ) {
        useMap().activeDatasetBoundary = newVal;
        this.boundaryTick();
      }
    },
    "dataSources.length": {
      handler(val) {
        if (val && !Object.keys(this.filterOperators).length) {
          useGammaData().fetchFilterOperators();
        }
      },
    },
  },
  mounted() {
    window.onresize = () => {
      if (!this.mapResizer) {
        this.mapResizer = () => {
          setTimeout(() => {
            this.map?.resize();
            this.mapResizer = null;
          });
        };
        this.map?.once("idle", this.mapResizer);
      }
    };
    this.$bus.$on("map:flyToLocation", this.flyToLocation);
    this.boundaryTick = debounce(() => {
      Array.from(this.onBoundsUpdated.values()).forEach((fn) => {
        if (typeof fn === "function") {
          fn.apply(this);
        }
      });
    }, 456);
  },
  beforeDestroy() {
    for (const hook in this.hooks) {
      if (Object.prototype.hasOwnProperty.call(this.hooks, hook)) {
        this.removeBusListener(hook);
      }
    }
  },
  methods: {
    updateDataBoundary(geometry: DataBoundary) {
      if (this.map) {
        this.dataSources.forEach(
          (name) => (useDataSource(name)().usePolygonFilter = !!geometry)
        );
        this.boundaryTick();
        (
          this.map.getSource("gammadata-boundary") as mapboxgl.GeoJSONSource
        ).setData(
          geometry
            ? {
                type: "FeatureCollection",
                features: [geometry],
              }
            : emptyGeoJSON
        );
      } else {
        this.onMapIdle.push(() => this.updateDataBoundary(geometry));
      }
    },
    async loadMap() {
      const style = await resolveBaseMapStyle(this.baseMap);
      if (this.map) {
        this.map.setStyle(style);
      } else {
        this.initMap(style);
      }
    },
    getMapPosition() {
      let mapOptions;
      // updating mapPosition from URL happens here
      const mapPosition: Partial<MapPosition> | undefined =
        getUrlQueryParam("mapPosition");
      if (mapPosition) {
        mapOptions = {
          ...pick(
            this.mapOptions,
            Object.keys(this.mapOptions).filter(
              (key) => !["bounds", ...Object.keys(mapPosition)].includes(key)
            )
          ),
          ...mapPosition,
        };
      } else {
        mapOptions = this.mapOptions;
      }
      return mapOptions;
    },
    initMap(style: mapboxgl.Style | string): void {
      useMap().isMapLoading = true;
      this.showMapControls = false;
      this.removeMap();
      if (this.$refs.map instanceof HTMLDivElement) {
        this.map = new mapboxgl.Map({
          style,
          ...this.mapPosition,
          ...this.getMapPosition(),
          container: this.$refs.map as HTMLElement,
        });
        useMap().map = this.map;

        this.map.on("contextmenu", this.onContextMenu);
        this.onMapIdle.push(() => {
          if (this.jbaAccuracy) {
            this.updateJbaAccuracy();
          }
          if (this.map) {
            this.mapDraw = this.initMapDraw(this.map);
            useMap().mapDraw = this.mapDraw;
            this.riskGeometry.init(this.map);
            // updating location from URL happens here
            const location = getUrlQueryParam("address") as
              | ILocation
              | undefined;

            if (location?.geometry && typeof location.geometry === "string") {
              location.geometry = JSON.parse(
                decodeURI((location as any).geometry)
              );
            }

            if (location?.address?.precisionLevel) {
              location.address.precisionLevel =
                typeof location.address.precisionLevel === "string"
                  ? Number(location.address.precisionLevel)
                  : location.address.precisionLevel;
            }

            if (location) {
              if (location.query) {
                this.patchUrlQuery(location).then((data) => {
                  useLocation().updateLocation(data);
                  this.addLocationMarker();
                });
              } else {
                useLocation().updateLocation(location, true);
                this.addLocationMarker();
              }
            }
          }
        });
        this.map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
        this.setMapboxCtrlHooks();
        this.$nextTick().then(() => {
          this.showMapControls = true;
        });

        const idleBounced = debounce(() => {
          if (this.map) {
            const center = this.map.getCenter();
            this.currentMapPosition = {
              center: [center.lng, center.lat],
              zoom: this.map.getZoom(),
              pitch: this.map.getPitch(),
              bearing: this.map.getBearing(),
            };
            useMap().screenBoundary = this.map
              ? GDT.mapBoundsToGeoJSONPolygon(this.map.getBounds())
              : null;
          }
        }, 1000);

        this.addBusListener(
          "map:layers:updateLayer",
          (layerControl: LayerControl) => this.updateLayers(layerControl)
        );
        this.addBusListener("filters-updated", this.boundaryTick);

        this.map.on("idle", () => idleBounced());
        this.map.on("style.load", () => this.onMapLoad());
      }
    },
    drawPolygonPopup(map: mapboxgl.Map) {
      const drawPloygonMessage = new mapboxgl.Popup({
        closeOnClick: false,
        closeButton: false,
        className: "draw-polygon-message",
        maxWidth: "400",
        anchor: "left",
      });
      map.on("mousemove", (e): void => {
        if (
          useMap().editingMode.mode === "draw_polygon" ||
          useMap().isDrawingRiskGeom
        ) {
          drawPloygonMessage.setLngLat(e.lngLat);
          drawPloygonMessage.addTo(map);
          if (useMap().isEditingRiskGeom) {
            drawPloygonMessage.setHTML(
              "<h1>Click outside the geometry to save shape</h1>"
            );
          }
        }
        if (useMap().editingMode.mode === "simple_select") {
          drawPloygonMessage.remove();
          drawPloygonMessage.setHTML("<h1>Click to start drawing shape</h1>");
        }
      });
      map.on("click", () => {
        if (
          useMap().editingMode.mode === "draw_polygon" &&
          !useMap().isDrawingRiskGeom
        ) {
          drawPloygonMessage.setHTML(
            "<h1>Double click to exit drawing mode and save shape</h1>"
          );
        }
      });
    },
    initMapDraw(map: mapboxgl.Map) {
      let modes = MapboxDraw.modes;
      modes = {
        ...enableModes({
          ...modes,
        }),
        draw_circle: DrawRadiusCircle,
        direct_select: patchDirectSelect(modes.direct_select),
        draw_polygon: patchDrawPolygon(modes.draw_polygon),
        draw_risk_polygon: RiskDrawPolygon,
      };
      const mapDraw = new MapboxDraw({
        displayControlsDefault: false,
        modes,
        styles,
      });
      this.drawPolygonPopup(map);
      map.addControl(mapDraw);
      return mapDraw;
    },
    async patchUrlQuery(data: ILocation): Promise<ILocation> {
      if (!this.dataSources.length) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(this.patchUrlQuery(data));
          }, 300);
        });
      }
      const ds = data?.lcId ? useDataSource(data.lcId)() : null;
      if (ds) {
        await ds.td();
        Object.keys(get(data, "query") as Record<string, any>).forEach(
          (key) => {
            set(
              data,
              ["query", key],
              castValueType(
                get(data, ["query", key]),
                ds.columnDefinitionsMap.get(key)?.type || "String"
              )
            );
          }
        );
      } else {
        console.error(`Malformed query: could not find dataset: ${data?.lcId}`);
      }
      return Promise.resolve(data);
    },
    loadLayerImages(
      imageDefinition: string | mapboxgl.StyleFunction | mapboxgl.Expression
    ): void {
      if (typeof imageDefinition === "string") {
        this.loadImage(imageDefinition);
      } else {
        (imageDefinition as mapboxgl.Expression).forEach((element) => {
          this.loadImageFromExpression(element);
        });
      }
    },
    loadImageFromExpression(item: any): void {
      const isUrl =
        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\\.][a-z0-9]+)*\.[a-z]{2,5}(:\d{1,5})?(\/.*)?$/m;
      if (typeof item === "string") {
        if (isUrl.test(item) || item.startsWith("assets")) {
          this.loadImage(item);
        }
      } else {
        item.forEach((child: any) => {
          if (
            typeof child === "string" &&
            (isUrl.test(child) || child.startsWith("assets"))
          ) {
            this.loadImage(child);
          }
        });
      }
    },
    loadImage(path: string): void {
      if (this.map) {
        this.map.loadImage(
          path.startsWith("assets") ? require(`../../${path}`) : path,
          (err: any, image: any) => {
            if (err) {
              console.error(err);
            }
            if (this.map && !this.map?.hasImage(path)) {
              this.map.addImage(path, image);
            }
          }
        );
      }
    },
    onMapLoad(): void {
      if (this.map) {
        useMeasuringTool().initMeasuringTool(this.map);
        useLocation().reloadLocation();
        useRiskGeometry().loadMeasurementLayers(this.map);

        this.mapSettings?.layers
          .filter((l) => l.type === "fill" && get(l.paint, "fill-pattern"))
          .forEach((p) => {
            const pattern = get(p.paint, "fill-pattern");
            if (pattern) this.loadLayerImages(pattern);
          });
        this.mapSettings?.layers
          .filter((l) => l.type === "symbol" && get(l.layout, "icon-image"))
          .forEach((p) => {
            const iconImage = get(p.layout, "icon-image");
            if (iconImage) this.loadLayerImages(iconImage);
          });
        this.addSources();
        this.addLayers();

        useLayout().isRightSidebarOpen =
          !this.orgSettings?.applicationSettings?.minifyRightSidebar;
        setTimeout(() => {
          useMap().isMapLoading = false;
        }, 123);
        this.onMapIdle.forEach((item) => {
          if (typeof item === "function") {
            item();
          }
        });
        setTimeout(() => {
          this.addDataSourcesInteractions();
        });
        this.onMapIdle = [];
        useMap().screenBoundary = this.map
          ? GDT.mapBoundsToGeoJSONPolygon(this.map.getBounds())
          : null;
      }
    },
    removeMap() {
      if (this.map) {
        const center = this.map.getCenter();
        useMap().setMapPosition({
          center: [center.lng, center.lat],
          zoom: this.map.getZoom(),
          pitch: this.map.getPitch(),
          bearing: this.map.getBearing(),
        });
        this.map.remove();
      }
    },
    addDataSourcesInteractions() {
      this.dataSources.forEach((name: string) => {
        const ds = useDataSource(name)();
        const layers = this.getLayersByLcId(ds.name);
        if (this.map && ds.name && layers.length > 0) {
          layers.forEach((layer) => {
            this.addLayerInteractions(ds.name, layer);
          });
        }
      });
    },
    handleMultiFeatures(
      e: mapboxgl.MapMouseEvent,
      features: mapboxgl.MapboxGeoJSONFeature[]
    ) {
      if (!this.map) {
        return;
      }

      const newZoom = this.map.getZoom() + 2;
      if (features.length > 6 && newZoom <= this.map.getMaxZoom()) {
        this.map.flyTo({
          zoom: newZoom,
          center: e.lngLat,
        });
        return;
      }

      this.addMapPopup(VerticalMenu, {
        coordinates: e.lngLat,
        removePopups: true,
        popupOptions: {
          closeOnClick: true,
          className: "accumulations-popup context-menu",
          anchor: "bottom",
        },
        dataGetter: () => ({
          items: features.map((feature) => this.createDataListItem(feature)),
        }),
      });
    },
    addLayerInteractions(source: string, layer: string) {
      if (this.map) {
        this.map.on("mousemove", layer, (e) => {
          if (this.isDrawToolOpen) {
            return;
          }
          this.map && (this.map.getCanvas().style.cursor = "pointer");
          if ((e.features?.length || 0) > 0) {
            if (this.hoveredFeatureId[layer]) {
              this.map &&
                this.map.setFeatureState(
                  { source, id: this.hoveredFeatureId[layer] },
                  { hover: false }
                );
            }
            this.hoveredFeatureId[layer] = get(e, "features[0].id");
            this.map &&
              this.map.setFeatureState(
                { source, id: this.hoveredFeatureId[layer] },
                { hover: true }
              );
          }
        });
        this.map.on("click", layer, (e) => {
          if (
            useMap().editLayer?.source === source &&
            useMap().editingMode.mode === "simple_select" &&
            e
          ) {
            this.handleEditLayerClick(layer, e);
            return;
          }

          if (this.map) {
            e.preventDefault();
            if (this.isDrawToolOpen || useMap().editableLayerSets.has(layer)) {
              return;
            }
            const bbox = [
              [e.point.x - 5, e.point.y - 5],
              [e.point.x + 5, e.point.y + 5],
            ] as [mapboxgl.PointLike, mapboxgl.PointLike];
            const contextualActiveLayers = this.layerControls
              .map((lc) => lc.activeGroup?.allLayers)
              .find((ctrl) => ctrl?.includes(layer));

            if (!contextualActiveLayers || contextualActiveLayers.length === 0)
              return;
            const features = this.map.queryRenderedFeatures(bbox, {
              layers: contextualActiveLayers,
            });
            if (features.length > 1) {
              this.handleMultiFeatures(e, features);
            } else if (features[0]) {
              useLocation().updateLocation({
                query: features[0].properties,
                lcId: features[0].source,
                geometry: features[0].geometry as Point,
                type: "Record",
              });
            }
          }
        });
        this.map.on("mouseleave", layer, () => {
          if (typeof this.hoveredFeatureId[layer] === "number") {
            this.map &&
              this.map.setFeatureState(
                { source, id: this.hoveredFeatureId[layer] },
                { hover: false }
              );
          }
          this.hoveredFeatureId[layer] = null;
          this.map && (this.map.getCanvas().style.cursor = "");
        });

        this.layersWithInteractions.add(layer);
      }
    },
    handleEditLayerClick(
      layer: string,
      e: mapboxgl.MapMouseEvent & {
        features?: mapboxgl.MapboxGeoJSONFeature[] | undefined;
      }
    ) {
      if (!e || !e.features?.length) {
        return;
      }
      const feature = e.features[0];
      if (feature.properties?.group_number) {
        useGroupedRecords().selectedGroup = feature.properties.group_number;
      }

      this.addMapPopup(VerticalMenu, {
        coordinates: e.lngLat,
        removePopups: true,
        popupOptions: {
          closeOnClick: true,
          className: "context-menu",
          anchor: "top-right",
        },
        dataGetter: () => ({
          items: [
            {
              action: async () => this.deleteGroupRecord(layer, feature),
              icon: "trash",
              text: "Delete",
            },
          ],
        }),
      });
    },

    async deleteGroupRecord(
      layer: string,
      feature: mapboxgl.MapboxGeoJSONFeature
    ) {
      if (!feature) {
        return;
      }
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Deleting ${feature ? feature?.properties?.group_number : ""}`,
        {
          title: "Are you sure?",
          okVariant: "danger",
          centered: true,
          size: "sm",
        }
      );

      if (confirmed && feature?.properties?.group_number) {
        await useGroupedRecords().delete(feature?.properties?.group_number);
      }

      useMap().refreshLayerData(layer).then();
      this.closeContextMenu();
    },

    setActiveRecord(feature: mapboxgl.MapboxGeoJSONFeature) {
      this.closeContextMenu();
      useLocation().updateLocation({
        query: feature.properties,
        lcId: feature.source,
        geometry: feature.geometry as Point,
        type: "Record",
      });
    },
    createDataListItem(feature: mapboxgl.MapboxGeoJSONFeature) {
      const isClaim = feature.layer.id.indexOf("_claim") > -1;
      const index = this.dataSources.findIndex(
        (lcId) => lcId === feature.source
      );

      const dataSource = useDataSource(feature.source)();
      const idColumns = index > -1 ? dataSource.uniqueIdColumns : [];
      const getFeatureText = (feat: mapboxgl.MapboxGeoJSONFeature) =>
        idColumns
          .map((col) => get(feat.properties, col.columnName) || "")
          .filter(Boolean)
          .join("/");

      const sumInsuredColName = dataSource.columnDefinitions.find(
        (x) => x.context === "SumInsured"
      )?.columnName;

      const sumInsuredVal =
        feature.properties && sumInsuredColName
          ? feature.properties[sumInsuredColName]
          : 0;

      const tooltipMessage = isClaim
        ? `Claim Incurred: ${formatValue(sumInsuredVal, {
            currency: getCurrencySign(this.currencySymbol),
          })}`
        : `Total Sum Insured: ${formatValue(sumInsuredVal, {
            currency: getCurrencySign(this.currencySymbol),
          })}`;

      return {
        action: () => this.setActiveRecord(feature),
        icon: isClaim
          ? getRagIconUrl(feature.properties?.type || "")
          : "file-earmark-text",
        isSvg: isClaim,
        text: getFeatureText(feature),
        tooltip: sumInsuredColName ? tooltipMessage : undefined,
      };
    },
    addSources() {
      if (this.map) {
        const sources = this.mapSettings?.sources as Record<
          string,
          GDT.GammaDataSourceRaw
        >;
        for (const name in sources) {
          if (Object.prototype.hasOwnProperty.call(sources, name)) {
            if (sources[name].type === "gammadata") {
              // const layer = this.mapSettings?.layers.find(
              //   (l: mapboxgl.Layer) => ("source" in l && l.source) === name
              // ) as mapboxgl.Layer;
              const layers =
                this.mapSettings?.layers
                  .filter(
                    (l: mapboxgl.Layer) => ("source" in l && l.source) === name
                  )
                  .map((l) => l.id) || [];
              if (layers.length) {
                addGammaDataSource({
                  name,
                  source: sources[name],
                  map: this.map,
                  layers,
                });
                this.$nextTick(() => {
                  useGammaData().addDataSource({
                    name,
                    source: sources[name],
                    layers,
                  });
                });
                useMap().addOnBoundsUpdated(name, () => {
                  const controls = this.layerControls.filter((_) =>
                    _.allLayerIds.some((layer) => layers.includes(layer))
                  );
                  if (this.map) {
                    const shouldUpdateData = controls.some(
                      (ctrl) => ctrl.isVisible
                    );
                    if (shouldUpdateData) {
                      this.$nextTick(() => {
                        if (this.map) {
                          updateGammaDataSourceData({
                            name,
                            source: sources[name],
                            map: this.map,
                            layers,
                            mapBoundsPolygon:
                              this.activeDatasetBoundary ||
                              this.mapBounds ||
                              undefined,
                            shouldLoad: true,
                          });
                        }
                      });
                    }
                  }
                });
              }
            } else if (!this.map.getSource(name)) {
              this.map.addSource(
                name,
                sources[name] as unknown as mapboxgl.AnySourceData
              );
            }
          }
        }
      }
    },
    addLayers() {
      if (this.map) {
        Object.keys(this.baseMap?.layerHooks || {}).forEach((id: string) => {
          this.map &&
            this.addLayer({
              id,
              type: "line",
              source: {
                type: "geojson",
                data: emptyGeoJSON,
              },
              metadata: {
                topLayer: get(this, ["baseMap", "layerHooks", id]),
              },
            });
        });
        const layers = this.mapSettings?.layers || [];
        layers.forEach((layer) => this.addLayer(layer));
        if (this.mapSettings?.JBA && this.map) {
          this.map.addSource("accuracy-buffer", {
            type: "geojson",
            data: emptyGeoJSON,
          } as mapboxgl.GeoJSONSourceRaw);
          this.addLayer(this.mapSettings.JBA.accuracyBuffer);
        }
        this.map.addSource("gammadata-boundary", {
          type: "geojson",
          data: this.dataBoundary || emptyGeoJSON,
        });
        this.map.addLayer({
          id: "gammadata-boundary-fill",
          type: "line",
          source: "gammadata-boundary",
          paint: {
            "line-color": "#10707f",
          },
        });
        this.map.addLayer({
          id: "gammadata-boundary-outline",
          type: "fill",
          source: "gammadata-boundary",
          paint: {
            "fill-color": "#3bb2d0",
            "fill-opacity": 0.1,
          },
        });
        (this.layerControls as LayerControl[]).forEach((lc) =>
          this.updateLayers(lc)
        );
      }
    },
    updateLayerVisibility(layer: mapboxgl.Layer) {
      if (this.map) {
        const layerControl = (this.layerControls as LayerControl[]).find((lc) =>
          lc.allLayerIds.includes(layer.id)
        );

        const layerSettings = this.mapSettings?.layers.find(
          (x) => x.id === layer.id
        );

        if (layerControl) {
          const opacityProps =
            layer.type === "symbol"
              ? ["icon"]
              : layer.type === "circle"
              ? ["circle", "circle-stroke"]
              : [layer.type];
          opacityProps.forEach((prop) => {
            const opacity = get(layerSettings, `paint.${prop}-opacity`) || 1;
            this.map?.setPaintProperty(
              layer.id,
              `${prop}-opacity`,
              layerControl.opacity * opacity
            );
          });

          // Sets visibility property. It is used somewhere to determent wether or not to load the source data :-(
          const visibility = layerControl.isStyleLayer
            ? layerControl.activeLayerId === layer.id && layerControl.isVisible
            : layerControl.isVisible;
          this.map.setLayoutProperty(
            layer.id,
            "visibility",
            visibility ? "visible" : "none"
          );
          if (this.isDataLayerControl(layerControl?.id)) {
            const sources = this.mapSettings?.sources as GDT.GammaDataSourceRaw;
            if (layer.source as string) {
              updateGammaDataSourceData({
                name: layer.source as string,
                source: sources[
                  layer.source as string
                ] as GDT.GammaDataSourceRaw,
                map: this.map,
                layers: [layer.id],
                mapBoundsPolygon: this.activeDatasetBoundary || undefined,
                shouldLoad: true,
              });
            }
          }
        }
      }
    },
    addLayer(layer: mapboxgl.Layer) {
      if (this.map) {
        this.map.addLayer(
          {
            ...layer,
            metadata: {
              ...("metadata" in layer ? layer.metadata : {}),
              keep: true,
            },
          } as mapboxgl.AnyLayer,
          layer.metadata?.topLayer
        );
      }
    },
    isDataLayerControl(lcId?: string) {
      return !!(lcId && this.dataSources.find((id) => id === lcId));
    },
    getLayersByLcId(lcId: string): string[] {
      let layers: string[] = [];
      if (this.map) {
        layers = ((this.map.getStyle().layers as mapboxgl.Layer[]) || [])
          .filter((l) => l.source === lcId)
          .flatMap((l) => l.id);
      }
      return layers;
    },
    updateLayers(layerControl: LayerControl) {
      this.$nextTick(() =>
        layerControl.allLayerIds.forEach((id) => {
          if (this.map) {
            const mapLayer = (this.map.getStyle().layers || []).find(
              (l) => l.id === id
            );

            if (mapLayer) {
              this.updateLayerVisibility(mapLayer);
            }
          }
        })
      );
    },
    addBusListener(hook: string, fn: (...args: any[]) => any) {
      this.removeBusListener(hook);
      this.hooks[hook] = this.$bus.$on(hook, fn);
    },
    removeBusListener(hook: string) {
      if (this.hooks[hook]) {
        this.$bus.$off(hook, this.hooks[hook]);
      }
    },
    onContextMenu(e: mapboxgl.MapMouseEvent) {
      this.contextObj = e;
      this.addMapPopup(VerticalMenu, {
        coordinates: e.lngLat,
        removePopups: true,
        popupOptions: {
          closeOnClick: true,
          className: "context-menu",
          anchor: "top-right",
        },
        dataGetter: () => {
          const menuItems = {
            items: [
              {
                action: () => this.queryMap(e.lngLat),
                icon: "chat-square-text",
                text: "What's here?",
              },
              {
                action: () => this.centerMap(e.lngLat),
                icon: "map",
                text: "Center map here",
              },
              {
                action: () => this.addMeasurementPoint(e.lngLat),
                icon: "arrows-angle-expand",
                text: "Measure distance",
              },
              {
                action: () => this.clearMeasurements(),
                icon: "trash",
                text: "Clear Measurements",
              },
              {
                action: () => this.resetOrientation(),
                icon: "mapboxgl-ctrl",
                text: "Reset map rotation",
              },
              {
                action: () => {
                  this.centerMap(e.lngLat);
                  this.openStreetViewModal();
                },
                icon: "card-image",
                text: "Street view",
              },
            ],
          };
          if (useOrgSettings().applicationSettings?.enableGeometryRisk) {
            menuItems.items.push(
              {
                action: () => this.addDrawCircle(e.lngLat),
                icon: "plus-circle-dotted",
                text: "Draw 200m circle",
              },
              ...(useMap().isDrawingRiskGeom
                ? [
                    {
                      action: () => this.editRiskGeometry(),
                      icon: "pencil-square",
                      text: "Edit Risk Geometry",
                    },
                    {
                      action: () => this.removeRiskGeometry(),
                      icon: "bounding-box-circles",
                      text: "Remove Risks Geometry",
                    },
                  ]
                : [
                    {
                      action: () => this.drawRiskGeometry(),
                      icon: "bounding-box-circles",
                      text: "Risks in Geometry",
                    },
                  ])
            );
          }
          return menuItems;
        },
      });
    },
    async openStreetViewModal() {
      this.$bvModal.show("street-view-modal");
    },
    addMeasurementPoint(lnglat: mapboxgl.LngLat) {
      this.closeContextMenu();
      useMeasuringTool().setPoint(lnglat);
    },
    queryMap(center: mapboxgl.LngLat) {
      this.mapTransitions = true;
      this.addLocationMarker({ center });
      this.resetHighlight();
      useLocation().updateLocation({
        type: "Coordinate",
        geometry: { type: "Point", coordinates: [center.lng, center.lat] },
      });
      this.closeContextMenu();
    },
    resetHighlight() {
      if (this.map) {
        (
          this.map.getSource("accuracy-buffer") as mapboxgl.GeoJSONSource
        ).setData(emptyGeoJSON);
      }
    },
    centerMap(center: mapboxgl.LngLat) {
      this.mapTransitions = true;
      this.setMapPosition({ center: [center.lng, center.lat] });
      this.closeContextMenu();
    },
    resetOrientation() {
      this.mapTransitions = true;
      this.setMapPosition({ ...this.currentMapPosition, pitch: 0, bearing: 0 });
      this.closeContextMenu();
    },
    addDrawCircle(center: mapboxgl.LngLat) {
      this.mapTransitions = true;
      this.setMapPosition({ center: [center.lng, center.lat], zoom: 14 });
      this.closeContextMenu();
      setTimeout(() => {
        requestAnimationFrame(() => {
          useMap().dataBoundary = null;
          const circle = createGeoJSONCircle([center.lng, center.lat], 0.2);
          this.updateBoundaryPolygon({
            features: circle.data.features as Feature<Polygon>[],
          });
          if (!useLayout().isBottomDrawerOpen) {
            useLayout().isBottomDrawerOpen = true;
            useLayout().bottomActiveTab = BottomTab.Accumulations;
          }
        });
      }, 456);
    },
    drawRiskGeometry() {
      this.riskGeometry.startDraw();
      this.closeContextMenu();
    },
    removeRiskGeometry() {
      this.riskGeometry.endDraw();
      useLocation().updateLocation(null);
      this.closeContextMenu();
    },
    editRiskGeometry() {
      this.riskGeometry.edit();
      this.closeContextMenu();
    },
    closeContextMenu() {
      if (this.popup) {
        this.popup.remove();
      }
    },
    setMapPosition(position: Partial<MapPosition>) {
      useMap().setMapPosition(position);
    },
    addLocationMarker(args: Partial<LocationMarker> = {}) {
      if (this.currentLocation && this.currentLocation.type === "Geometry") {
        return;
      }

      let options = {
        coordinates:
          args.center || get(this.currentLocation, "geometry")?.coordinates,
      } as any;
      const draggable = useMap().isEditingCoordinates;
      if (draggable && useMap().markerPosition) {
        options = { coordinates: useMap().markerPosition };
      }
      const markerComponent = MapMarker;
      let component: null | CombinedVueInstance<any, any, any, any, any>;
      if (this.map && options.coordinates) {
        if (this.locationMarker) {
          this.locationMarker.remove();
        }
        component = addComponent(this, markerComponent, () => ({
          color: draggable ? "#ff1b41" : "#006699",
          draggable,
        }));
        this.locationMarker = new mapboxgl.Marker(
          options.markerOptions || {
            draggable,
            element: component.instance.$el,
            anchor: "bottom",
            offset: [0, 8],
          }
        )
          .setLngLat(options.coordinates)
          .addTo(this.map)
          .on("close", () => component.dispose());
        useMap().markerPosition = this.locationMarker?.getLngLat() || null;
        if (draggable) {
          this.locationMarker.on("drag", () => {
            useMap().markerPosition = this.locationMarker?.getLngLat() || null;
          });
        }
      }
    },
    addMapPopup(popupComponent: unknown, options: any) {
      let component: null | CombinedVueInstance<any, any, any, any, any>;
      if (this.map) {
        if (this.popup && options.removePopups) {
          this.popup.remove();
        }
        component = addComponent(this, popupComponent, options.dataGetter);
        this.popup = new mapboxgl.Popup(
          options.popupOptions || {
            closeOnClick: false,
          }
        )
          .setLngLat(options.coordinates)
          .setDOMContent(component.instance.$el)
          .addTo(this.map)
          .on("close", () => component.dispose());
      }
      return {
        component,
        popup: this.popup,
      };
    },
    updateJbaAccuracy() {
      const val = this.jbaAccuracy;
      if (this.map && !this.map.getSource("accuracy-buffer")) {
        this.map.once("idle", () => this.updateJbaAccuracy());
        return;
      }

      const numPoints = val?.BufferType === "square" ? 4 : 64;
      if (this.map) {
        if (!val) {
          this.resetHighlight();
        } else if (val.Level === "BuildingZoomstack" && val.BuildingGeom) {
          (
            this.map.getSource("accuracy-buffer") as mapboxgl.GeoJSONSource
          ).setData({ ...val.BuildingGeom });
        } else if (val.Level === "PointBuffer") {
          (
            this.map.getSource("accuracy-buffer") as mapboxgl.GeoJSONSource
          ).setData(
            val?.BufferType === "square"
              ? createGeoJSONSquare(
                  useLocation().coordinates,
                  val.BufferMeters ? val.BufferMeters / 1e3 : 0.05
                ).data
              : createGeoJSONCircle(
                  useLocation().coordinates,
                  val.BufferMeters ? val.BufferMeters / 1e3 : 0.05,
                  numPoints
                ).data
          );
        }
      }
    },
    clearMeasurements(): void {
      this.closeContextMenu();
      this.map && useMeasuringTool().clearMeasurements();
    },
    updateBoundaryPolygon(event: { features: Feature<Polygon>[] }): void {
      if (get(event, "features[0].geometry.type") !== "LineString") {
        const feature = get(event, "features[0]") as Feature<Polygon>;
        if (feature && this.map) {
          useMap().updateBoundaryPolygon(feature);
        }
        this.mapDraw.deleteAll();
        this.refreshDrawMode(this.mapDraw.getMode());
      }
    },
    refreshDrawMode(mode: string): void {
      if (mode === "direct_select" && this.dataBoundary) {
        const featureIds = this.mapDraw.add(wrapDrawFeature(this.dataBoundary));
        useMap().updateBoundaryPolygon(null);
        this.mapDraw.changeMode("direct_select", { featureId: featureIds[0] });
      } else {
        if (mode === "simple_select") {
          const features = (this.mapDraw?.getAll().features || []).filter(
            (f: Feature<Polygon>) => f.geometry.coordinates[0][0]
          );
          if (!features.length) {
            return;
          }
          useMap().updateBoundaryPolygon(features[0]);
          this.mapDraw.deleteAll();
          if (this.mapDraw.getMode() !== "simple_select") {
            this.mapDraw.changeMode("simple_select");
          }
        } else if (mode !== "direct_select") {
          this.mapDraw.changeMode("simple_select");
          this.mapDraw.changeMode(mode);
          useMap().updateBoundaryPolygon(null);
        }
      }
    },
    updateRouteQuery(to: Route) {
      ["address", "mapPosition"].forEach((key) => {
        if (to.query[key] && typeof to.query[key] === "string") {
          const val = JSON.parse(to.query[key] as string);
          if (!isEqual(val, get(this, key))) {
            set(this, "current" + pascalCase(key), val || null);
          }
        }
      });
    },
    setMapboxCtrlHooks() {
      [
        {
          container: ".mapboxgl-ctrl-bottom-left",
          hook: "mgl-ctrl-bottom-left",
        },
        {
          container: ".mapboxgl-ctrl-top-right",
          hook: "mgl-ctrl-top-right",
        },
      ].forEach((item) => {
        const container = this.$el?.querySelector(item.container);
        if (container && !container.querySelector(`#${item.hook}`)) {
          const div = document.createElement("div");
          div.id = item.hook;
          container.appendChild(div);
        }
      });
    },
    toggleLeftSidebar() {
      useLayout().isLeftSidebarOpen = !useLayout().isLeftSidebarOpen;
    },
    flyToLocation() {
      this.addLocationMarker();
      this.mapTransitions = true;
      this.currentMapPosition = {
        ...this.currentMapPosition,
        zoom: Math.max(Math.min(this.map ? this.map.getZoom() : 10, 17), 18),
      };
    },
  },
});
